// @ts-check
import api from './api'
import { calculateShifts } from '@bluecrew/bluecrew-lib'
import { clone, equals, find, findIndex, map, prop, reduce, propEq, update, values, mapObjIndexed, pluck } from 'ramda'
import moment from 'moment-timezone'

const manageJobs = ($http, $q, Auth) => {
  'ngInject'
  let jobs = []

  const getManagedJobsByCompanyId = (companyId, cancelledJobs) => {
    const url = `${api.getBase()}/routes/manage-jobs`
    return $http({
      method: 'GET',
      url: url,
      params: {
        companyId,
      }
    }).then(prop("data"))
      .then(item => map(setNumShifts, item))
      .then(item => map(setNumOnJob, item))
  };
  
  const getManagedJobs = (startDate, region, fetchInterviews, cancelledJobs, accountManager, accountManagers) => {
    const url = `${api.getBase()}/routes/manage-jobs`;

    startDate = moment(startDate).startOf('day').toDate();
    if(accountManager) {
      return $http({
        method: 'GET',
        url: url,
        params: {
          startDate: startDate,
          region: region || null,
          fetchInterviews: fetchInterviews || null,
          cancelledJobs: cancelledJobs || null,
          accountManager: accountManager || null
        }
      })
      .then(prop('data'))
      .then(item => values(item))
      .then(item => map(setNumShifts, item))
      .then(item => map(setNumOnJob, item))
      .then(setJobs)
    } else {
      const jobPromises = [];
      accountManagers.forEach(manager => {
        const jobPromise = $http({
          method: 'GET',
          url: url,
          params: {
            startDate: startDate,
            region: region || null,
            fetchInterviews: fetchInterviews || null,
            cancelledJobs: cancelledJobs || null,
            accountManager: manager.id
          }
        })
        .then(prop('data'))
        .then(item => values(item))
        .then(item => map(setNumShifts, item))
        .then(item => map(setNumOnJob, item));;
        jobPromises.push(jobPromise);
      });

      return Promise.all(jobPromises).then(jobs => {
        jobs = jobs.flat();
        return setJobs(jobs);
      });
    }
  }

  const getManagedJobsV3 = (startDate, region, cancelledJobs, accountManager) => {
    startDate = moment(startDate).startOf('day').toDate()
    return $http({
      method: 'GET',
      url: `${api.getBase()}/routes/manage-jobs-kernel`,
      params: {
        startDate: startDate,
        region: region || null,
        cancelledJobs: cancelledJobs || null,
        accountManager: accountManager || null
      }
    })
    .then(prop('data'))
    .then(item => values(item))
    .then(item => map(setNumShifts, item))
    .then(item => map(setNumOnJob, item))
    .then(setJobs)
  }

  /**
   * Call to get the number of unique dispatch views assocaited with a set of jobs
   * @param [array] externalIds
   */
  const getJobsSentReadCount = (externalIds) => {
    return $http({
      method: 'POST',
      url: `${api.getBase()}/job/getSentReadCount`,
      data: {
        externalIds
      }
    })
    .then(prop('data'))
  }

  const setJobs = (data) => {
    jobs = data
    return jobs
  }

  const getJob = (id) => find(propEq('job_id', id))(jobs)

  const deleteJob = (id) => {
    const index = findIndex(propEq('job_id', id))(jobs)
    jobs.splice(index, 1)
    return jobs
  }

  // this should probably be broken up
  const setJobVal = (id, updatedProps) => {
    let def = $q.defer()

    let job = clone(getJob(id))
    let index = findIndex(propEq('job_id', id))(jobs)

    let updateJobParam = (value, prop, obj) => {
      job[prop] = value
    }

    mapObjIndexed(updateJobParam, updatedProps)
    setNumOnJob(job)
    console.log('modified job', job)
    jobs = update(index, job, jobs)
    def.resolve(jobs)
    return def.promise
  }

  const setNumShifts = (job) => {
    let start = moment(job.start_date_time)
    let end = moment(job.end_date_time)
    job.num_shifts = calculateShifts(start, end, job.days, 'America/Los_Angeles').length
    return job
  }

  const setNumOnJob = (job) => {
    let pplOnJob = 0
    let passedInterview = 0
    if (job.attachedUsers) {
      pplOnJob = reduce((a, b) => {
        if (equals(b.status, 0)) return a + 1
        return a
      }, 0, values(job.attachedUsers))
      passedInterview = reduce((a, b) => {
        if (equals(b.status, 11)) return a + 1
        return a
      }, 0, values(job.attachedUsers))
    }
    job.passedInterview = passedInterview
    job.pplOnJob = pplOnJob
    return job
  }

  /**
   * @param {object} data
   * @param {number} data.jobId - the internal job ID
   * @param {number} data.newWage - the new wage dollar amount
   */
  const increaseWage = (data) => {
    return $http({
      method: 'POST',
      url: `${api.getBase()}/job/increaseWage`,
      data: {
        jobId: data.jobId,
        newWage: data.newWage,
        clientApproved: data.clientApproved
      }
    })
  }

  /**
   * Pause or resume a job notifying users.
   *
   * @param {object} data
   * @param {string} data.jobId
   * @param {boolean} data.pause - pause (true) or resume (false) a job blast.
   */
  const pauseJob = (data) => {
    return $http({
      method: 'POST',
      url: `${api.getBase()}/job/update/status`,
      data})
  }

  /**
   * Cancels a job.
   *
   * @param {object} data
   * @param {string} data.jobId - the external job ID
   */
  const cancelJob = (data) => {
    return $http({
      method: 'POST',
      url: `${api.getBase()}/job/cancel`,
      data})
  }

  /**
   * Update the 3 job requirements for a job.
   *
   * @param {object} data
   * @param {number} data.jobId - the internal job ID
   * @param {string[]} data.requirements - the new reqs
   */
  const updateReqs = (data) => {
    return $http({
      method: 'POST',
      url: `${api.getBase()}/job/update/requirements`,
      data})
  }

  /**
   * Yes/no to reminder texts being sent for a given job.
   *
   * @param {object} data
   * @param {string} data.jobId - the external job ID
   * @param {boolean} data.reminder
   */
  const updateReminders = (data) => {
    return $http({
      method: 'POST',
      url: `${api.getBase()}/job/update/reminderTexts`,
      data})
  }

  /**
   * Set the job as application required or not.
   *
   * @param {object} data
   * @param {string} data.jobId - the external job ID
   * @param {boolean} data.isApplication - whether the job requires application.
   */
  const updateApplication = (data) => {
    return $http({
      method: 'POST',
      url: `${api.getBase()}/job/update/isApplication`,
      data})
  }

  /**
   * Send a job out to the whole platform or not.
   *
   * @param {object} data
   * @param {string} data.jobId - the external job ID
   * @param {boolean} data.wholePlatform
   */
  const wholePlatform = (data) => {
    return $http({
      method: 'POST',
      url: `${api.getBase()}/job/update/wholePlatform`,
      data})
  }

  const startNickAlgo = (data) => {
    return $http({
      method: 'POST',
      url: `${api.getBase()}/job/startNickExternalId`,
      data
    })
  }

  /**
   *
   * @param {object} data
   * @param {array} data.updateUsers - external ids of users
   */

  const updateJob = (data) => {
    return $http({
      method: 'POST',
      contentType: 'application/json',
      url: `${api.getBase()}/job/changeAppliedStatus`,
      data
    })
  }

  /**
   *
   * @param {object} data
   * @param {string} data.start - internal job id
   */
  const getJobTimeline = (data) => {
    return $http({
      method: 'POST',
      contentType: 'application/json',
      url: `${api.getBase()}/job/activity`,
      data
    })
    .then(prop('data'))
  }

  const updateDetails = (data) => {
    return $http({
      method: 'POST',
      contentType: 'application/json',
      url: `${api.getBase()}/job/update/updateDetailsV2`,
      data
    })
  }

  const updateNumberNeeded = (data) => {
    return $http({
      method: 'POST',
      contentType: 'application/json',
      url: `${api.getBase()}/job/update/numberNeeded`,
      data
    })
  }

  const updateOverfill = (data) => {
    return $http({
      method: 'POST',
      contentType: 'application/json',
      url: `${api.getBase()}/job/update/overfill`,
      data
    })
  }

  const sendReminderEmail = (data) => {
    return $http({
      method: 'POST',
      contentType: 'application/json',
      url: `${api.getBase()}/job/fill/emailClient`,
      data
    })
  }

  const getRosterPDF = (day, job) => {
    let company = job.company_name.replace(/[^a-zA-Z ]/g, '').replace(/\s+/g, '-')
    let date = moment(day).format('YYYY-MM-DD')
    let url = `${api.getBase()}/roster/${company}-${date}.pdf`

    return $http({
      method: 'POST',
      url,
      responseType: 'arraybuffer'
    })
  }

  const getRosterCSV = (job) => {
    let url = `${api.getBase()}/job/fill/generateCSV?jobId=${job.external_id}`
    return $http({
      method: 'POST',
      url
    })
  }

  return {
    getManagedJobsByCompanyId,
    getManagedJobs,
    getManagedJobsV3,
    getJobsSentReadCount,
    getJobTimeline,
    pauseJob,
    updateJob,
    updateReqs,
    updateReminders,
    updateApplication,
    cancelJob,
    wholePlatform,
    startNickAlgo,
    increaseWage,
    getRosterCSV,
    getRosterPDF,
    updateDetails,
    updateNumberNeeded,
    updateOverfill,
    sendReminderEmail,
    setJobVal,
    deleteJob
  }
}

manageJobs.$inject = ['$http', '$q', 'Auth']

export default manageJobs
